import { computed, makeObservable, observable, runInAction } from 'mobx';
import { AgenturStore } from '../agentur.store';
import { ISelectOpt } from 'app/components/common/Select';
import { formatBM } from 'app/utils';
import { AgenturModel } from 'app/models/agentur.model';
import _ from 'lodash';
import { BaseUiStore } from './base.ui.store';
import { AgtTiktokStore, AgtTiktokModel } from '../agt.tiktok.store';

export class TiktokBm {
	bm: number;
	screenName: string;
	status: number;
	follower: number;
	views: number;
	reach: number;
	posts: number;

	constructor(bm: number, screenName: string, status: number, follower: number, views: number, reach: number, posts: number) {
		this.bm = bm;
		this.screenName = screenName;
		this.status = status;
		this.follower = follower;
		this.views = views;
		this.reach = reach;
		this.posts = posts;
	}
}

export class AgtTiktokVM {
	agt: AgenturModel;
	@observable
	tiktoks: TiktokBm[] = [];

	prevYearBm: number = 0;
	currYearBm: number = 0;
	currYear: number = 0;

	constructor(agt: AgenturModel, tiktoks: AgtTiktokModel[]) {
		makeObservable(this);
		this.agt = agt;
		const bms = [];
		for (const kv of tiktoks) {
			const j = kv.j;
			const tk = new TiktokBm(kv.bm, j.n, j.s, j.f, j.v, j.r, j.p);
			bms.push(kv.bm);
			this.tiktoks.push(tk);
		}
		if (bms.length > 0) {
			this.currYearBm = _.max(bms)!;
			this.currYear = Math.round(this.currYearBm / 100);
			this.prevYearBm = (this.currYear - 1) * 100 + 12;
		}
	}

	@computed
	get prevTiktok() {
		return this.tiktoks.find((t) => t.bm === this.prevYearBm);
	}

	@computed
	get currTiktok() {
		return this.tiktoks.reduce<TiktokBm | undefined>((max, tk) => (tk.bm > (max?.bm || 0) ? tk : max), undefined);
	}

	@computed
	get prevFollower() {
		return this.prevTiktok ? this.prevTiktok.follower : 0;
	}

	@computed
	get currFollower() {
		return this.currTiktok ? this.currTiktok.follower : 0;
	}

	@computed
	get postsYtd() {
		return this.tiktoks.reduce((acc, t) => {
			if (t.bm >= this.currYear * 100 && t.posts) {
				return acc + t.posts;
			}
			return acc;
		}, 0);
	}

	@computed
	get viewsYtd() {
		return this.tiktoks.reduce((acc, t) => {
			if (t.bm >= this.currYear * 100 && t.views) {
				return acc + t.views;
			}
			return acc;
		}, 0);
	}

	@computed
	get reachYtd() {
		return this.tiktoks.reduce((acc, t) => {
			if (t.bm >= this.currYear * 100 && t.reach) {
				return acc + t.reach;
			}
			return acc;
		}, 0);
	}
}

export class AgtTiktokUiStore extends BaseUiStore<AgtTiktokVM> {
	name = 'AgtTiktokUiStore';
	agtTiktokStore: AgtTiktokStore;
	agenturStore: AgenturStore;

	constructor(agenturStore: AgenturStore, agtTiktokStore: AgtTiktokStore) {
		super();
		makeObservable(this);
		this.agtTiktokStore = agtTiktokStore;
		this.agenturStore = agenturStore;
	}

	@observable
	bms: number[] = [];

	@observable
	minBmCurrent?: number;

	@observable
	maxBmCurrent?: number;

	@computed
	get currentYear() {
		const mb = this.maxBm;
		if (mb === undefined) {
			return '';
		}
		return Math.floor(mb / 100);
	}

	@computed
	get maxBm() {
		if (this.maxBmCurrent) return this.maxBmCurrent;
		return _.max(this.bms);
	}

	@computed
	get minBm() {
		if (this.minBmCurrent) return this.minBmCurrent;
		return _.min(this.bms);
	}

	@computed
	get bmOpts(): ISelectOpt[] {
		return this.bms.map((bm) => {
			return {
				value: bm,
				label: formatBM(bm),
			};
		});
	}

	@observable
	tiktokBms: number[] = [];

	async _baseLoad() {
		const tiktokBms = await this.agtTiktokStore.getDistinctBMs();
		this.tiktokBms = tiktokBms;

		runInAction(() => {
			this.bms = tiktokBms.sort();
		});
	}

	async loadByMaxBmForYear(yearBm: number) {
		const cacheKey = `AgtTiktokUiStore.loadByBm${this.maxBm}-${this.minBm}`;

		return this.withCache(
			cacheKey,
			async () => {
				await this.load();

				const maxBmorEndofYear = (bm: number) => {
					if (bm > (yearBm + 1) * 100) return yearBm * 100 + 12;
					return bm;
				};

				const maxTiktokBm = maxBmorEndofYear(_.max(this.tiktokBms)!);

				const [tiktoks, agts] = await Promise.all([
					this.agtTiktokStore.loadByBm(maxTiktokBm),
					this.agenturStore.findAll()
				]);

				const res: AgtTiktokVM[] = [];
				for (const agt of agts) {
					let tk = tiktoks.filter((d) => d.agtId === agt.agtId);
					const vm = new AgtTiktokVM(agt, tk);
					res.push(vm);
				}

				runInAction(() => {
					this.items = res;
				});

				return this.items;
			},
			{
				cacheResult: true,
				measureTime: true,
			}
		);
	}
}
